import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
} from 'react-native';

export function useCustomAlertModal(onHiddenModal) {
  let timer;
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error' or 'success'
    onConfirm: null
  });

  const showCustomAlert = (title, message, type = 'error', onConfirm = null, options = {}) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });

    const { timeout } = options;
    if (timeout) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }

      timer = setTimeout(() => {
        hideCustomAlert();
        timer = null;
      }, timeout);
    }
  };

  const hideCustomAlert = () => {
    setAlertModal(prev => ({ ...prev, visible: false }));

    if( onHiddenModal ) {
      onHiddenModal();
    }
  };

  const handleCustomAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
  };

  const headerStyle = (type) => {
    if ( type === 'error' ) return styles.errorHeader;
    if ( type === 'info' ) return styles.infoHeader;
    return styles.successHeader;
  };

  const buttonStyle = (type) => {
    if ( type === 'error' ) return styles.errorButton;
    if ( type === 'info' ) return styles.infoButton;
    return styles.successButton;
  };

  const customAlertModal = (
    <Modal
      animationType="fade"
      transparent={true}
      visible={alertModal.visible}
      onRequestClose={hideCustomAlert}
    >
      <View style={styles.customModalOverlay}>
        <View style={styles.customModalContainer}>
          <View style={[styles.customModalHeader, headerStyle(alertModal.type)]}>
            <Text style={styles.customModalTitle}>{alertModal.title}</Text>
          </View>
          <View style={styles.customModalBody}>
            <Text style={styles.customModalMessage}>{alertModal.message}</Text>
          </View>
          <View style={styles.customModalFooter}>
            {alertModal.onConfirm ? (
              <View style={styles.customModalButtonsRow}>
                <TouchableOpacity 
                  style={[styles.customModalButton, styles.cancelButton]}
                  onPress={hideCustomAlert}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.customModalButton, buttonStyle(alertModal.type)]}
                  onPress={handleCustomAlertConfirm}
                >
                  <Text style={styles.customModalButtonText}>{alertModal.type === 'success' ? 'OK' : 'Confirm'}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity 
                style={[styles.customModalButton, buttonStyle(alertModal.type)]}
                onPress={hideCustomAlert}
              >
                <Text style={styles.customModalButtonText}>OK</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );

  return { customAlertModal, showCustomAlert, hideCustomAlert, handleCustomAlertConfirm };
}

const styles = StyleSheet.create({
  customModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    zIndex: 11000,
  },
  customModalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 320,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  customModalHeader: {
    padding: 20,
    paddingBottom: 15,
  },
  successHeader: {
    backgroundColor: '#10B981',
  },
  errorHeader: {
    backgroundColor: '#EF4444',
  },
  infoHeader: {
    backgroundColor: "#3B82F6",
  },
  customModalTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  customModalBody: {
    padding: 20,
    paddingTop: 10,
    paddingBottom: 15,
  },
  customModalMessage: {
    fontFamily: 'Poppins',
    fontWeight: '400',
    fontSize: 14,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 20,
  },
  customModalFooter: {
    padding: 20,
    paddingTop: 10,
  },
  customModalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  customModalButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  successButton: {
    backgroundColor: '#10B981',
    flex: 1,
  },
  errorButton: {
    backgroundColor: '#EF4444',
    flex: 1,
  },
  infoButton: {
    backgroundColor: "#3B82F6",
    flex: 1,
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    flex: 1,
  },
  customModalButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#FFFFFF',
    fontSize: 14,
  },
  cancelButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#6B7280',
    fontSize: 14,
  },
});